import React from 'react';
import { Link } from 'react-router-dom';
import { contactInfo } from '../data/mock';
import { Phone, Mail, MapPin, ArrowUp } from 'lucide-react';

const Footer = () => {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-slate-900 text-white">
      {/* Main footer */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <img 
                src="/media/logo-cmjn-long-3dloc-high-95e288e8.jpg" 
                alt="3DLoc - Location de matériel" 
                className="h-12 w-auto object-contain bg-white rounded-lg p-2"
              />
            </Link>
            <p className="text-slate-400 mb-6">
              La location dans 3 Dimensions. Professionnels & Particuliers, à vos côtés du matériel au TOP pour donner vie à vos projets.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.facebook.com/3dlocation" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center hover:bg-green-500 transition-colors"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Nos Univers</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/univers/evenementiel-barnum" className="text-slate-400 hover:text-green-400 transition-colors">
                  Evénementiel & Loisirs
                </Link>
              </li>
              <li>
                <Link to="/univers/bricolage-travaux" className="text-slate-400 hover:text-green-400 transition-colors">
                  Bricolage & Bâtiment
                </Link>
              </li>
              <li>
                <Link to="/univers/jardinage-motoculture" className="text-slate-400 hover:text-green-400 transition-colors">
                  Motoculture & Jardin
                </Link>
              </li>
              <li>
                <Link to="/univers/nettoyage-logement" className="text-slate-400 hover:text-green-400 transition-colors">
                  Nettoyage & Logement
                </Link>
              </li>
            </ul>
          </div>

          {/* Useful Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Informations</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/zones-livraison" className="text-slate-400 hover:text-green-400 transition-colors">
                  Zones de livraison
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-slate-400 hover:text-green-400 transition-colors">
                  Questions fréquentes
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-slate-400 hover:text-green-400 transition-colors">
                  Demander un devis
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-slate-400 hover:text-green-400 transition-colors">
                  Nos services
                </Link>
              </li>
              <li>
                <Link to="/univers" className="text-slate-400 hover:text-green-400 transition-colors">
                  Notre catalogue
                </Link>
              </li>
              <li>
                <Link to="/actualites" className="text-slate-400 hover:text-green-400 transition-colors">
                  Actualités
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-bold mb-6">Contact</h4>
            <ul className="space-y-4">
              <li>
                <a href={`tel:${contactInfo.phone}`} className="flex items-center gap-3 text-slate-400 hover:text-green-400 transition-colors">
                  <Phone size={18} />
                  {contactInfo.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-3 text-slate-400 hover:text-green-400 transition-colors">
                  <Mail size={18} />
                  {contactInfo.email}
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-slate-400">
                  <MapPin size={18} className="flex-shrink-0 mt-1" />
                  <span>{contactInfo.address}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4">
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} 3DLoc. Tous droits réservés.
            </p>
            <Link to="/mentions-legales" data-testid="footer-mentions-legales" className="text-slate-400 hover:text-green-400 text-sm transition-colors">
              Mentions légales
            </Link>
            <Link to="/conditions-generales-location" data-testid="footer-conditions-generales" className="text-slate-400 hover:text-green-400 text-sm transition-colors">
              Conditions générales de location
            </Link>
            <Link to="/avis-clients" data-testid="footer-avis-clients" className="text-slate-400 hover:text-green-400 text-sm transition-colors">
              Avis clients
            </Link>
            <Link to="/guides" data-testid="footer-guides" className="text-slate-400 hover:text-green-400 text-sm transition-colors">
              Guides pratiques
            </Link>
          </div>
          <button
            onClick={scrollToTop}
            className="group flex items-center gap-2 text-slate-400 hover:text-green-400 transition-colors"
          >
            <span className="text-sm">Retour en haut</span>
            <div className="w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center group-hover:bg-green-500 transition-colors">
              <ArrowUp size={16} />
            </div>
          </button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
